import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Gift, ShieldCheck } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { VaultCoin } from "@/components/vault";
import { cn } from "@/lib/utils";

/**
 * 3D highlighted amount hero for the Deposit page.
 * Shows the amount the user is about to deposit in a dramatic card
 * with a floating coin and glowing numeric type. Falls back to a
 * neutral "enter an amount" state when `amount` is 0/empty.
 */
export default function DepositHero({ amount, currency = "INR", bonus = 0, method }) {
  const n = Number(amount) || 0;
  const ready = n > 0;
  const total = n + Number(bonus || 0);

  return (
    <div
      className={cn(
        "relative mb-5 overflow-hidden rounded-2xl border border-amber-200 p-5 sm:p-6",
        "bg-gradient-to-br from-slate-950 via-[#0f1734] to-slate-900 text-white",
        "shadow-[0_20px_60px_hsl(222_60%_12%/0.3)]"
      )}
      data-testid="deposit-hero"
      data-amount={n}
    >
      {/* Radial golden glow */}
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full"
        style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.35) 0%, transparent 65%)" }}
      />
      <div
        className="pointer-events-none absolute -left-24 bottom-0 h-56 w-56 rounded-full"
        style={{ background: "radial-gradient(circle, hsl(186 72% 48% / 0.3) 0%, transparent 65%)" }}
      />

      <div className="relative grid grid-cols-1 items-center gap-4 sm:grid-cols-[1fr_auto]">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-300/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-200">
            <Sparkles className="h-3 w-3" /> You're depositing
          </span>

          <div className="mt-3" style={{ perspective: 800 }}>
            <motion.p
              key={`${currency}:${n}`}
              initial={{ opacity: 0, rotateX: -25, y: 10 }}
              animate={{ opacity: 1, rotateX: 0, y: 0 }}
              transition={{ duration: 0.4, ease: [0.2, 0.9, 0.2, 1] }}
              className="num origin-top text-5xl font-bold tracking-tight sm:text-6xl"
              style={{
                color: ready ? "hsl(44 100% 76%)" : "hsl(220 15% 60%)",
                textShadow: ready
                  ? "0 2px 0 hsl(36 80% 32%), 0 8px 24px hsl(44 86% 60% / 0.45), 0 0 2px hsl(44 100% 80%)"
                  : "none",
                transformStyle: "preserve-3d",
              }}
              data-testid="deposit-hero-amount"
            >
              {ready ? fmtMoney(n, currency) : currency === "INR" ? "\u20B9 0.00" : "0.0000 USDT"}
            </motion.p>
          </div>

          {ready && bonus > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 flex flex-wrap items-center gap-2 text-sm"
              data-testid="deposit-hero-bonus"
            >
              <span className="inline-flex items-center gap-1 rounded-md bg-amber-300/20 px-2 py-0.5 text-xs font-medium text-amber-200">
                <Gift className="h-3 w-3" /> Bonus +{fmtMoney(bonus, currency)}
              </span>
              <span className="text-xs text-slate-300">
                Credit after approval: <span className="num font-semibold text-white">{fmtMoney(total, currency)}</span>
              </span>
            </motion.div>
          )}
          {!ready && (
            <p className="mt-3 text-sm text-slate-300" data-testid="deposit-hero-empty">
              Enter the amount below &mdash; we'll show your bonus and the total credit live.
            </p>
          )}
          {method && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-300" data-testid="deposit-hero-method">
              <ShieldCheck className="h-3 w-3 text-emerald-300" />
              Paying via <span className="font-medium text-white">{method.name}</span>
            </p>
          )}
        </div>

        <div className="relative hidden sm:block" aria-hidden="true">
          <div
            className="absolute left-1/2 top-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.35) 0%, transparent 60%)" }}
          />
          <VaultCoin size={128} paused={!ready} />
        </div>
      </div>
    </div>
  );
}
